import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Check, Zap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

export default function Subscriptions() {
  const { publicSettings, user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(null);

  const tiers = [
    { key: 'free', name: 'Free', price: 0, features: ['Basic AI chat & tools', 'Pay-as-you-go video credits', 'Standard profile', 'Basic rewards'], accent: 'from-slate-500 to-slate-400' },
    { key: 'premium_monthly', name: 'Premium', price: publicSettings?.sub_premium_price ?? 9.99, features: publicSettings?.sub_premium_features || [], accent: 'from-violet-500 to-fuchsia-500', popular: true },
    { key: 'elite_monthly', name: 'Elite', price: publicSettings?.sub_elite_price ?? 24.99, features: publicSettings?.sub_elite_features || [], accent: 'from-amber-400 to-yellow-500' },
  ];

  const subscribe = async (lookup_key) => {
    if (!user) { toast.error('Please sign in first'); nav('/login'); return; }
    if (lookup_key === 'free') return;
    setLoading(lookup_key);
    try {
      const { data } = await api.post('/payments/checkout', { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout unavailable — try again shortly');
      setLoading(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="text-center">
        <h1 className="font-display text-4xl font-black">Jixxeo AI Membership</h1>
        <p className="text-slate-400 mt-2">Visibility, status, and exclusive benefits. Cancel anytime.</p>
        <p className="text-xs text-slate-500 mt-2">
          Looking for AI video generation? That's <button onClick={() => nav('/credits')} className="underline text-amber-300 hover:text-amber-200" data-testid="subs-credits-link">pay-as-you-go video credits</button> — separate from subscription.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tiers.map(t => {
          const isCurrent = user && (
            (t.key === 'free' && !user.is_premium) ||
            (t.key === 'premium_monthly' && user.premium_tier === 'premium') ||
            (t.key === 'elite_monthly' && user.premium_tier === 'elite')
          );
          return (
            <Card key={t.key} data-testid={`plan-${t.name.toLowerCase()}`} className={`glass p-6 relative ${t.popular ? 'ring-2 ring-violet-500' : ''}`}>
              {t.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 gradient-brand rounded-full text-xs font-bold">MOST POPULAR</div>}
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${t.accent} flex items-center justify-center`}><Zap className="w-5 h-5 text-white" /></div>
              <div className="font-display text-2xl font-bold mt-3">{t.name}</div>
              <div className="font-display text-4xl font-black mt-2">${t.price}<span className="text-sm font-normal text-slate-400">/mo</span></div>
              <div className="text-[11px] text-slate-500 mt-1">{t.name === 'Free' ? 'Starter membership' : 'Membership benefits'}</div>
              <ul className="mt-4 space-y-2 text-sm">
                {t.features.map(f => <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />{f}</li>)}
              </ul>
              <Button
                data-testid={`subscribe-${t.name.toLowerCase()}`}
                disabled={isCurrent || loading === t.key}
                className={`w-full mt-5 ${t.key === 'free' ? 'bg-white/10 hover:bg-white/15 text-white' : 'gradient-brand text-white'}`}
                onClick={() => subscribe(t.key)}
              >
                {loading === t.key ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</> : (isCurrent ? 'Current plan' : (t.price > 0 ? `Subscribe — $${t.price}/mo` : 'Get started'))}
              </Button>
            </Card>
          );
        })}
      </div>
      <p className="text-center text-xs text-slate-500">
        Cancel anytime from your account.
      </p>
    </div>
  );
}
